import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Upload, Loader2, AlertTriangle, Monitor, Smartphone } from 'lucide-react';
import { useNotifications } from '../../hooks/useNotifications';
import { ActionButton, Button } from '../UI';

export type HeroVideoFormat = 'landscape' | 'portrait';

export interface HeroVideo {
  url?: string;
  posterUrl?: string;
  durationSec?: number;
  width?: number;
  height?: number;
  status?: '' | 'processing' | 'ready' | 'failed';
  error?: string;
}

export type HeroVideos = Partial<Record<HeroVideoFormat, HeroVideo>>;

interface Props {
  /** Sem id (LP ainda não salva) os envios ficam desabilitados: as rotas gravam no documento. */
  landingPageId?: string;
  value: HeroVideos;
  onChange: (next: HeroVideos) => void;
  /** Upload antigo (hero.video.url), exibido no slot horizontal até ser substituído. */
  legacyUrl?: string;
}

const MAX_BYTES = 250 * 1024 * 1024;
const POLL_MS = 5000;

const SLOTS: { format: HeroVideoFormat; title: string; hint: string; Icon: typeof Monitor }[] = [
  { format: 'landscape', title: 'Horizontal (16:9)', hint: 'Computador', Icon: Monitor },
  { format: 'portrait', title: 'Vertical (9:16)', hint: 'Celular', Icon: Smartphone }
];

/** Upload dos vídeos do hero por formato. O servidor converte (ffmpeg) e o slot acompanha o processamento. */
const HeroVideoSlots: React.FC<Props> = ({ landingPageId, value, onChange, legacyUrl }) => {
  const notifications = useNotifications();
  const [uploading, setUploading] = useState<HeroVideoFormat | null>(null);
  const inputRefs = useRef<Record<HeroVideoFormat, HTMLInputElement | null>>({ landscape: null, portrait: null });
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const anyProcessing = SLOTS.some(({ format }) => value[format]?.status === 'processing');

  useEffect(() => {
    if (!landingPageId || !anyProcessing) return;
    const timer = setInterval(() => {
      axios.get(`/landing-pages/${landingPageId}`)
        .then((res) => onChangeRef.current(res.data.heroVideos || {}))
        .catch(() => {});
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [landingPageId, anyProcessing]);

  const upload = async (format: HeroVideoFormat, file: File) => {
    if (!landingPageId) return;
    if (file.size > MAX_BYTES) {
      notifications.showWarning('Vídeo muito grande', 'O vídeo deve ter no máximo 250 MB.');
      return;
    }
    try {
      setUploading(format);
      const data = new FormData();
      data.append('video', file);
      await axios.post(`/landing-pages/${landingPageId}/hero-video/${format}`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange({ ...value, [format]: { ...value[format], status: 'processing', error: '' } });
    } catch (error: any) {
      notifications.showError('Erro ao enviar vídeo', error.response?.data?.message || 'Tente novamente.');
    } finally {
      setUploading(null);
      const input = inputRefs.current[format];
      if (input) input.value = '';
    }
  };

  const remove = async (format: HeroVideoFormat) => {
    if (!landingPageId) return;
    try {
      await axios.delete(`/landing-pages/${landingPageId}/hero-video/${format}`);
      onChange({ ...value, [format]: {} });
    } catch (error: any) {
      notifications.showError('Erro ao remover vídeo', error.response?.data?.message || 'Tente novamente.');
    }
  };

  if (!landingPageId) {
    return (
      <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3">
        Salve a landing page (rascunho) para enviar os vídeos.
      </p>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {SLOTS.map(({ format, title, hint, Icon }) => {
          const video = value[format] || {};
          const isLegacy = format === 'landscape' && !video.url && !!legacyUrl;
          const src = video.url || (isLegacy ? legacyUrl : '');
          const processing = video.status === 'processing';
          const busy = uploading === format || processing;
          const mismatch = !!video.url && !!video.width && !!video.height
            && (format === 'landscape' ? video.height > video.width : video.width > video.height);
          return (
            <div key={format} className="border border-gray-200 rounded-lg p-3 flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <Icon className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-900">{title}</span>
                <span className="text-xs text-gray-500">· {hint}</span>
              </div>

              <div className={`${format === 'landscape' ? 'aspect-video' : 'aspect-[9/16] max-h-80 mx-auto'} w-full bg-gray-100 rounded-md overflow-hidden flex items-center justify-center`}>
                {src ? (
                  <video key={src} src={src} poster={video.posterUrl || undefined} controls playsInline preload="metadata" className="w-full h-full object-contain bg-black" />
                ) : (
                  <span className="text-xs text-gray-400 px-2 text-center">Sem vídeo</span>
                )}
              </div>

              {isLegacy && <p className="text-xs text-gray-500">Vídeo atual (envio antigo, sem conversão).</p>}
              {processing && (
                <p className="text-xs text-primary-700 inline-flex items-center gap-1.5">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Processando… {video.url ? 'O vídeo anterior segue no ar até terminar.' : 'Pode levar alguns minutos.'}
                </p>
              )}
              {video.status === 'failed' && video.error && (
                <p className="text-xs text-red-700 inline-flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{video.error}
                </p>
              )}
              {mismatch && (
                <p className="text-xs text-amber-700 inline-flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  Este vídeo está na {format === 'landscape' ? 'vertical' : 'horizontal'}. Confira se enviou no lugar certo.
                </p>
              )}

              <input
                ref={(el) => { inputRefs.current[format] = el; }}
                type="file"
                accept="video/*"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(format, f); }}
              />
              <div className="flex flex-wrap gap-2 mt-auto">
                <Button variant="secondary" size="sm"
                  type="button"
                  disabled={busy}
                  onClick={() => inputRefs.current[format]?.click()}>
                  {uploading === format ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploading === format ? 'Enviando…' : src ? 'Trocar' : 'Enviar vídeo'}
                </Button>
                {video.url && (
                  <ActionButton
                    action="delete"
                    label="Remover"
                    labelVisibility="always"
                    disabled={busy}
                    onClick={() => remove(format)}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-gray-500 mt-3">
        Até 3 min / 250 MB cada (MP4, MOV ou WebM). O servidor converte para MP4 720p com capa.
        Se enviar só um, ele é usado em todas as telas.
      </p>
    </div>
  );
};

export default HeroVideoSlots;
